import {MarkerSpot, RulesLogic, Spot, SpotType} from "../../components/types";

export const formatMillisecondsAsTimeCode = (valueMs?: number) => {
    const safeMs = Math.max(0, Math.round(valueMs || 0));
    const minutes = Math.floor(safeMs / 60000);
    const seconds = Math.floor((safeMs % 60000) / 1000);
    const tenths = Math.floor((safeMs % 1000) / 100);
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}:${tenths}`;
};

/**
 * Create a Spot object suitable for use as a cue entry.
 * Uses SpotType.Marker with a dummy position since cue spots don't need map placement.
 */
export const createCueSpot = (areaId: string, name: string, cueIndex: number, runOnes: boolean = true): MarkerSpot => ({
    id: "",
    area: areaId,
    name: formatCueName(cueIndex, name),
    type: SpotType.Marker,
    // Cue list sequencing is derived from rules + name prefix, not Spot priority.
    priority: 0,
    beaconSensitivity: 8,
    pauseOtherAmbients: false,
    followMainSound: false,
    playMode: "play",
    playFullAfter: 0,
    loop: false,
    runOnes: runOnes,
    modifiedAt: 0,
    key: "",
    position: {lat: 0, lng: 0}
});

/**
 * Format a cue display name with zero-padded index prefix.
 * e.g., formatCueName(1, "Welcome") → "001 - Welcome"
 */
export const formatCueName = (index: number, label: string): string => {
    const padded = String(index + 1).padStart(3, "0");
    return `${padded} - ${label}`;
};

/**
 * Extract the user-facing label from a cue spot name.
 * e.g., "001 - Welcome" → "Welcome", "My Spot" → "My Spot"
 */
export const getCueLabel = (spot: Spot): string => {
    const match = /^\d{2,}\s*-\s*(.+)$/.exec(spot.name || "");
    return match ? match[1] : (spot.name || "");
};

const getCueSequence = (spot: Spot): number | undefined => {
    const match = /^\s*(\d+)\s*-\s*/.exec(spot.name || "");
    if (!match) {
        return undefined;
    }
    const value = parseInt(match[1], 10);
    return Number.isFinite(value) ? value : undefined;
};

const hasSharedTrigger = (spot: Spot) => {
    return !!(spot.beaconTriggers?.length || spot.wiFiTriggers?.length);
};

const isPositiveSequencingEventType = (eventType?: string) => {
    return eventType === "SPOT_FINISHED"
        || eventType === "SPOT_TRIGGERED"
        || eventType === "SPOT_FINISHED_FOR_MS"
        || eventType === "AMBIENT_TRIGGERED"
        || eventType === "AMBIENT_TIME_SINCE_TRIGGERED";
};

const isNegativeSpotEventType = (eventType?: string) => {
    return eventType === "SPOT_NOT_TRIGGERED" || eventType === "SPOT_NOT_FINISHED";
};

const isNegativeEventType = (eventType?: string) => {
    return isNegativeSpotEventType(eventType) || eventType === "AMBIENT_NOT_TRIGGERED";
};

const hasPositiveSpotDependency = (spot: Spot) => {
    const rules = spot.triggerRules?.rules || [];
    return rules.some((rule) => rule.type === "Spot" && isPositiveSequencingEventType(rule.eventType));
};

const hasPositiveDependency = (spot: Spot) => {
    const rules = spot.triggerRules?.rules || [];
    return rules.some((rule) => isPositiveSequencingEventType(rule.eventType));
};

const hasOnlyNegativeSpotRules = (spot: Spot) => {
    const rules = spot.triggerRules?.rules || [];
    if (rules.length === 0) {
        return false;
    }
    return rules.every((rule) => isNegativeEventType(rule.eventType));
};

const getRulePredecessorId = (spot: Spot, byKey: Map<string, Spot>) => {
    const rules = spot.triggerRules?.rules || [];
    const spotRule = rules.find((rule) => rule.type === "Spot" && rule.eventType === "SPOT_FINISHED")
        || rules.find((rule) => rule.type === "Spot" && rule.eventType === "SPOT_TRIGGERED")
        || rules.find((rule) => rule.type === "Spot" && rule.eventType === "SPOT_FINISHED_FOR_MS");
    if (!spotRule) {
        return undefined;
    }
    if (spotRule.spotKey) {
        return byKey.get(spotRule.spotKey)?.id;
    }
    return undefined;
};

const cueSort = (a: Spot, b: Spot) => {
    // Entries that can be triggered immediately should be shown earliest.
    // This includes spots with no rules, spots with only negative rules,
    // and spots that only depend on ambient zones (not on other spots).
    const aRules = a.triggerRules?.rules || [];
    const bRules = b.triggerRules?.rules || [];
    const aIsImmediate = aRules.length === 0 || hasOnlyNegativeSpotRules(a) || (hasPositiveDependency(a) && !hasPositiveSpotDependency(a));
    const bIsImmediate = bRules.length === 0 || hasOnlyNegativeSpotRules(b) || (hasPositiveDependency(b) && !hasPositiveSpotDependency(b));
    const aImmediateTier = aIsImmediate ? 0 : 1;
    const bImmediateTier = bIsImmediate ? 0 : 1;
    if (aImmediateTier !== bImmediateTier) {
        return aImmediateTier - bImmediateTier;
    }

    const aHasPositiveDependency = hasPositiveSpotDependency(a);
    const bHasPositiveDependency = hasPositiveSpotDependency(b);
    if (aHasPositiveDependency !== bHasPositiveDependency) {
        return aHasPositiveDependency ? 1 : -1;
    }

    const aHasTrigger = hasSharedTrigger(a);
    const bHasTrigger = hasSharedTrigger(b);
    if (aHasTrigger !== bHasTrigger) {
        return aHasTrigger ? -1 : 1;
    }
    const aSeq = getCueSequence(a) ?? Number.MAX_SAFE_INTEGER;
    const bSeq = getCueSequence(b) ?? Number.MAX_SAFE_INTEGER;
    if (aSeq !== bSeq) {
        return aSeq - bSeq;
    }
    const aName = a.name || "";
    const bName = b.name || "";
    if (aName !== bName) {
        return aName.localeCompare(bName);
    }
    return (a.id || "").localeCompare(b.id || "");
};

/**
 * Order cues from SPOT_FINISHED / SPOT_TRIGGERED rules (topological order).
 * Name sequence is only used as stable fallback feedback order.
 */
export const spotsToOrderedCues = (allSpots: Spot[]): Spot[] => {
    const spots = [...allSpots];
    if (spots.length <= 1) {
        return spots;
    }

    const byId = new Map(spots.map((spot) => [spot.id, spot]));
    const byKey = new Map(spots.filter((spot) => !!spot.key).map((spot) => [spot.key, spot]));
    const inDegree = new Map<string, number>(spots.map((spot) => [spot.id, 0]));
    const nextBySpotId = new Map<string, Spot[]>();

    spots.forEach((spot) => {
        const predecessorId = getRulePredecessorId(spot, byKey);
        if (!predecessorId || !byId.has(predecessorId) || predecessorId === spot.id) {
            return;
        }
        inDegree.set(spot.id, (inDegree.get(spot.id) || 0) + 1);
        const next = nextBySpotId.get(predecessorId) || [];
        next.push(spot);
        nextBySpotId.set(predecessorId, next);
    });

    const roots = spots
        .filter((spot) => (inDegree.get(spot.id) || 0) === 0)
        .sort(cueSort);

    // Follow each chain to completion before starting the next root.
    // This keeps spots within an ambient section together.
    const ordered: Spot[] = [];
    const visited = new Set<string>();
    const followChain = (spot: Spot) => {
        if (visited.has(spot.id)) return;
        visited.add(spot.id);
        ordered.push(spot);
        const next = (nextBySpotId.get(spot.id) || []).sort(cueSort);
        next.forEach((nextSpot) => {
            const degree = (inDegree.get(nextSpot.id) || 0) - 1;
            inDegree.set(nextSpot.id, degree);
            if (degree === 0) {
                followChain(nextSpot);
            }
        });
    };
    roots.forEach(followChain);

    if (ordered.length < spots.length) {
        const remaining = spots
            .filter((spot) => !ordered.some((orderedSpot) => orderedSpot.id === spot.id))
            .sort(cueSort);
        ordered.push(...remaining);
    }

    return ordered;
};

/**
 * Build SPOT_FINISHED trigger rule chains for an ordered list of cue spots.
 * - The first cue keeps whatever beacon/wifi triggers it has (no rule added).
 * - Each subsequent cue gets a triggerRule pointing to the previous cue.
 *
 * Returns an array of partial spot updates (id + triggerRules) to apply.
 */
export const buildTriggerChain = (
    orderedCues: Spot[]
): Array<{ spot: Spot; updates: Partial<Spot> }> => {
    return orderedCues.map((spot, index) => {
        const updates: Partial<Spot> = {};

        if (index === 0) {
            // First cue: clear sequencing rules (it has the real trigger)
            if (spot.triggerRules?.rules?.some(r => isPositiveSequencingEventType(r.eventType))) {
                updates.triggerRules = {
                    rules: spot.triggerRules.rules.filter(r => !isPositiveSequencingEventType(r.eventType)),
                    logic: spot.triggerRules.logic
                };
            }
        } else {
            const previousSpot = orderedCues[index - 1];
            updates.triggerRules = {
                rules: [{
                    id: `chain_${spot.id}`,
                    type: "Spot",
                    eventType: "SPOT_FINISHED",
                    spotKey: previousSpot.key
                }],
                logic: RulesLogic.AND
            };
        }

        // Update the name prefix to reflect current position
        const label = getCueLabel(spot);
        updates.name = formatCueName(index, label);

        return {spot, updates};
    });
};

/**
 * Get the next cue index based on existing spots.
 */
export const getNextCueIndex = (spots: Spot[]): number => {
    if (spots.length === 0) {
        return 0;
    }
    let maxSequence = 0;
    spots.forEach((spot) => {
        const sequence = getCueSequence(spot);
        if (sequence && sequence > maxSequence) {
            maxSequence = sequence;
        }
    });
    if (maxSequence > 0) {
        // Sequence is 1-based, cue index is 0-based.
        return maxSequence;
    }
    return spots.length;
};
